import React from 'react';
import {
  Badge,
  BlockStack,
  Button,
  Card,
  Checkbox,
  DataTable,
  Icon,
  InlineStack,
  Pagination,
  Select,
  Spinner,
  Text,
  TextField
} from '@shopify/polaris';
import { RefreshIcon, ReplayIcon, SearchIcon } from '@shopify/polaris-icons';
import { payoutReviewApi } from '../../services/api/payoutReviewApi';
import { formatUtcToTz } from '../../utils/timezone';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const PAGE_SIZE = 20;
const ACKNOWLEDGEABLE_REASONS = new Set([
  'invoice_closed',
  'payout_line_not_invoice_payment',
  'po_deduction_requires_review'
]);
const REASON_DETAILS = {
  invoice_closed: {
    label: 'Invoice already closed',
    action: 'Verify the existing payment and recover its CartNXS transaction mapping.'
  },
  amount_mismatch: {
    label: 'Amount mismatch',
    action: 'Compare the Wayfair payout with the NetSuite open invoice balance.'
  },
  missing_invoice: {
    label: 'Invoice not found',
    action: 'Create or recover the NetSuite invoice before retrying.'
  },
  missing_match_key: {
    label: 'Missing match key',
    action: 'Add the PO or invoice identity required to locate the NetSuite transaction.'
  },
  currency_mismatch: {
    label: 'Currency mismatch',
    action: 'Correct the payout or invoice currency configuration before retrying.'
  },
  payout_line_not_invoice_payment: {
    label: 'Settlement fee or deduction',
    action: 'Pair with its payment or process through settlement accounting.'
  },
  missing_customer_payment: {
    label: 'Customer payment not created',
    action: 'Resolve the invoice issue; this amount is temporarily held in Settlement Clearing.'
  },
  missing_credit_memo_or_customer_refund: {
    label: 'Return transaction not found',
    action: 'Create or recover the Credit Memo or Customer Refund; the deposit amount is held in Settlement Clearing.'
  },
  po_deduction_requires_review: {
    label: 'PO deduction needs review',
    action: 'Classify the deduction as a return, fee, or adjustment; it is temporarily held in Settlement Clearing.'
  },
  needs_review: {
    label: 'Needs review',
    action: 'Inspect the payout and corresponding NetSuite transaction before retrying.'
  }
};

function reasonDetail(reason) {
  return REASON_DETAILS[reason] || {
    label: String(reason || 'Needs review').replaceAll('_', ' '),
    action: 'Inspect the payout and corresponding NetSuite transaction before retrying.'
  };
}

function formatMoney(value, currency = 'USD') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 'Not set';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
  } catch (_) {
    return `${amount.toFixed(2)} ${currency || ''}`.trim();
  }
}

function retryIdentity(record, service) {
  return service === 'payout_settlements' ? record.settlementReference : record.poNumber;
}

function recordKey(record) {
  return String(record.id || record.settlementReference || record.poNumber || '');
}

export function PayoutReviewPanel({ service, onRetry, retryDisabled = false }) {
  const isSettlementService = service === 'payout_settlements';
  const [view, setView] = React.useState('lines');
  const [records, setRecords] = React.useState([]);
  const [summary, setSummary] = React.useState({});
  const [pagination, setPagination] = React.useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = React.useState(1);
  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [selected, setSelected] = React.useState(new Set());
  const [loading, setLoading] = React.useState(false);
  const [retrying, setRetrying] = React.useState(false);
  const [acknowledging, setAcknowledging] = React.useState(false);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const { currentWorkspace } = useWorkspace();
  const timezone = currentWorkspace?.config?.timezone || 'America/New_York';

  const loadReviews = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await payoutReviewApi.getReviews({ service, view, page, limit: PAGE_SIZE, search, reason });
      setRecords(response.records || []);
      setSummary(response.summary || {});
      setPagination(response.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [page, reason, search, service, view]);

  React.useEffect(() => {
    loadReviews();
    const timer = setInterval(loadReviews, 30000);
    return () => clearInterval(timer);
  }, [loadReviews]);

  React.useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [reason, search, service, view]);

  const toggleSelected = (record) => {
    const key = recordKey(record);
    if (!key || !retryIdentity(record, service)) return;
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectableRecords = records.filter((record) => retryIdentity(record, service));
  const allSelected = selectableRecords.length > 0 && selectableRecords.every((record) => selected.has(recordKey(record)));
  const toggleAll = () => {
    setSelected((current) => {
      const next = new Set(current);
      if (allSelected) selectableRecords.forEach((record) => next.delete(recordKey(record)));
      else selectableRecords.forEach((record) => next.add(recordKey(record)));
      return next;
    });
  };

  const retrySelected = async () => {
    const ids = [...new Set(records
      .filter((record) => selected.has(recordKey(record)))
      .map((record) => retryIdentity(record, service))
      .filter(Boolean))];
    if (!ids.length) return;

    setRetrying(true);
    setError('');
    setNotice('');
    try {
      await onRetry({ entityIds: ids, ids, timestamp: new Date().toISOString() });
      setNotice(`${ids.length} ${isSettlementService ? 'settlement' : 'PO'} ${ids.length === 1 ? 'retry' : 'retries'} queued.`);
      setSelected(new Set());
      window.setTimeout(loadReviews, 2000);
    } catch (retryError) {
      setError(retryError.message);
    } finally {
      setRetrying(false);
    }
  };

  const selectedRecords = records.filter((record) => selected.has(recordKey(record)));
  const canAcknowledgeSelected = selectedRecords.length > 0 && selectedRecords.every((record) => (
    view === 'lines'
      ? ACKNOWLEDGEABLE_REASONS.has(record.reason)
      : record.reasons?.length && record.reasons.every((item) => ACKNOWLEDGEABLE_REASONS.has(item.reason))
  ));
  const acknowledgeSelected = async () => {
    const values = selectedRecords
      .map((record) => view === 'settlements' ? record.settlementReference : record.id)
      .filter(Boolean);
    const label = view === 'settlements' ? 'payout settlement' : 'payout review record';
    if (!values.length || !window.confirm(`Close ${values.length} ${label}${values.length === 1 ? '' : 's'}? This will not change NetSuite transactions.`)) return;
    setAcknowledging(true);
    setError('');
    setNotice('');
    try {
      await payoutReviewApi.acknowledge(view === 'settlements'
        ? { settlementReferences: values }
        : { ids: values });
      setNotice(`${values.length} ${label}${values.length === 1 ? '' : 's'} closed.`);
      setSelected(new Set());
      await loadReviews();
    } catch (acknowledgeError) {
      setError(acknowledgeError.message);
    } finally {
      setAcknowledging(false);
    }
  };

  const reasonOptions = [
    { label: 'All review reasons', value: '' },
    ...Object.entries(REASON_DETAILS).map(([value, detail]) => ({ label: detail.label, value }))
  ];

  const lineRows = records.map((record) => {
    const detail = reasonDetail(record.reason);
    return [
      <Checkbox
        key={`select-${recordKey(record)}`}
        label={`Select ${record.poNumber || record.id}`}
        labelHidden
        checked={selected.has(recordKey(record))}
        disabled={!retryIdentity(record, service)}
        onChange={() => toggleSelected(record)}
      />,
      record.poNumber || 'Not set',
      record.settlementReference || 'Not set',
      record.payoutDate || 'Not set',
      String(record.lineType || 'Not set').replaceAll('_', ' '),
      formatMoney(record.amount ?? record.netAmount, record.currency),
      <BlockStack key={`reason-${recordKey(record)}`} gap="050">
        <Badge>{detail.label}</Badge>
        <Text variant="bodySm" tone="subdued">{detail.action}</Text>
        {record.varianceAmount != null && (
          <Text variant="bodySm" tone="critical">Variance: {formatMoney(record.varianceAmount, record.currency)}</Text>
        )}
        {record.payoutAmount != null && record.invoiceOpenAmount != null && (
          <Text variant="bodySm" tone="subdued">
            Payout {formatMoney(record.payoutAmount, record.currency)}; invoice open {formatMoney(record.invoiceOpenAmount, record.currency)}
          </Text>
        )}
      </BlockStack>,
      record.updatedAt ? formatUtcToTz(record.updatedAt, timezone) : 'Not set'
    ];
  });

  const settlementRows = records.map((record) => [
    <Checkbox
      key={`select-${recordKey(record)}`}
      label={`Select ${record.settlementReference || record.id}`}
      labelHidden
      checked={selected.has(recordKey(record))}
      disabled={!record.settlementReference}
      onChange={() => toggleSelected(record)}
    />,
    record.settlementReference || 'Not set',
    record.payoutDate || 'Not set',
    String(record.reviewCount || 0),
    formatMoney(record.netAmount, 'USD'),
    <BlockStack key={`reasons-${recordKey(record)}`} gap="050">
      {(record.reasons || []).map((item) => (
        <Text key={item.reason} variant="bodySm">
          {reasonDetail(item.reason).label}: {item.count}
        </Text>
      ))}
    </BlockStack>,
    (record.poNumbers || []).join(', ') || 'Not set',
    record.updatedAt ? formatUtcToTz(record.updatedAt, timezone) : 'Not set'
  ]);

  const totalReviewLines = summary.totalReviewLines || 0;
  const totalReviewSettlements = summary.totalReviewSettlements || 0;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text variant="headingMd" fontWeight="bold">Needs Review</Text>
            <Badge tone={totalReviewLines > 0 ? undefined : 'success'}>{totalReviewLines}</Badge>
          </InlineStack>
          <InlineStack gap="200" blockAlign="center">
            {loading && <Spinner size="small" />}
            <Button icon={RefreshIcon} variant="tertiary" onClick={loadReviews} disabled={loading}>Refresh</Button>
          </InlineStack>
        </InlineStack>

        {isSettlementService && (
          <InlineStack gap="200">
            <Button pressed={view === 'lines'} onClick={() => setView('lines')}>Review records</Button>
            <Button pressed={view === 'settlements'} onClick={() => setView('settlements')}>Settlement summary</Button>
          </InlineStack>
        )}

        <InlineStack gap="300" blockAlign="end" wrap>
          <div style={{ minWidth: '280px', flex: '1 1 360px' }} onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setSearch(searchInput.trim());
            }
          }}>
            <TextField
              label="Search review records"
              labelHidden
              value={searchInput}
              onChange={setSearchInput}
              prefix={<Icon source={SearchIcon} tone="subdued" />}
              placeholder="Search PO, invoice, settlement, or reason"
              autoComplete="off"
              maxLength={100}
            />
          </div>
          <div style={{ minWidth: '220px' }}>
            <Select label="Review reason" labelHidden options={reasonOptions} value={reason} onChange={setReason} />
          </div>
          <Button onClick={() => setSearch(searchInput.trim())}>Search</Button>
          <Button
            icon={ReplayIcon}
            variant="primary"
            disabled={selected.size === 0 || retryDisabled || retrying}
            loading={retrying}
            onClick={retrySelected}
          >
            Retry selected
          </Button>
          <Button
            disabled={!canAcknowledgeSelected || acknowledging}
            loading={acknowledging}
            onClick={acknowledgeSelected}
          >
            Close selected
          </Button>
        </InlineStack>

        {error && <Text tone="critical">{error}</Text>}
        {notice && <Text tone="success">{notice}</Text>}

        {!loading && records.length === 0 ? (
          <Text tone="subdued">No payout records currently require review.</Text>
        ) : (
          <DataTable
            columnContentTypes={view === 'settlements'
              ? ['text', 'text', 'text', 'numeric', 'numeric', 'text', 'text', 'text']
              : ['text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
            headings={view === 'settlements'
              ? [
                  <Checkbox label="Select all settlements" labelHidden checked={allSelected} onChange={toggleAll} />,
                  'Settlement', 'Date', 'Review lines', 'Net total', 'Reasons', 'PO numbers', 'Last reviewed'
                ]
              : [
                  <Checkbox label="Select all review records" labelHidden checked={allSelected} onChange={toggleAll} />,
                  'PO number', 'Settlement', 'Date', 'Line type', 'Amount', 'Reason and action', 'Last reviewed'
                ]}
            rows={view === 'settlements' ? settlementRows : lineRows}
            increasedTableDensity
          />
        )}

        {view === 'settlements' && totalReviewSettlements > 0 && (
          <Text variant="bodySm" tone="subdued">{totalReviewSettlements} settlements contain unresolved payout records.</Text>
        )}

        {(pagination.totalPages || 1) > 1 && (
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="bodySm" tone="subdued">
              Page {pagination.page || page} of {pagination.totalPages}; {pagination.total || 0} records
            </Text>
            <Pagination
              label={`Payout review page ${pagination.page || page} of ${pagination.totalPages}`}
              hasPrevious={page > 1}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              hasNext={page < pagination.totalPages}
              onNext={() => setPage((current) => Math.min(pagination.totalPages, current + 1))}
            />
          </InlineStack>
        )}
      </BlockStack>
    </Card>
  );
}

export { reasonDetail, retryIdentity };
